import { onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useKlaroConsent } from './useKlaroConsent'
import { useAnalyticsConfig } from './useAnalyticsConfig'

export function useGoogleAnalytics() {
  const route = useRoute()
  const { hasGoogleAnalyticsConsent, hasGoogleAdsConsent } = useKlaroConsent()
  const { shouldLoadGoogleAnalytics } = useAnalyticsConfig()

  // Sprawdź czy gtag jest dostępny
  const isGtagAvailable = () => {
    return typeof window !== 'undefined' && window.gtag
  }

  // Sprawdź czy użytkownik wyraził zgodę na Google Analytics
  const hasConsent = () => {
    return hasGoogleAnalyticsConsent() && shouldLoadGoogleAnalytics.value
  }

  // Śledź widok strony
  const trackPageView = (title = null, location = null) => {
    if (!hasConsent() || !isGtagAvailable()) {
      return
    }

    const pageTitle = title || document.title
    const pageLocation = location || window.location.href

    window.gtag('config', 'G-BV4PRKXHZR', {
      page_title: pageTitle,
      page_location: pageLocation
    })
  }

  // Śledź zdarzenie
  const trackEvent = (action, category, label = null, value = null) => {
    if (!hasConsent() || !isGtagAvailable()) return

    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value
    })
  }

  // Śledź kliknięcie w ofertę
  const trackOfferClick = (offerId, offerTitle) => {
    trackEvent('offer_click', 'engagement', offerTitle, offerId)
  }

  // Śledź wyszukiwanie
  const trackSearch = (query, resultsCount) => {
    trackEvent('search', 'engagement', query, resultsCount)
  }

  // Śledź dodanie do ulubionych
  const trackFavorite = (offerId, offerTitle, isAdd) => {
    trackEvent(isAdd ? 'add_favorite' : 'remove_favorite', 'engagement', offerTitle, offerId)
  }

  // Śledź dodanie do porównania
  const trackComparison = (offerId, offerTitle, isAdd) => {
    trackEvent(isAdd ? 'add_comparison' : 'remove_comparison', 'engagement', offerTitle, offerId)
  }

  // Śledź logowanie
  const trackLogin = (method) => {
    trackEvent('login', 'authentication', method)
  }

  // Śledź rejestrację
  const trackRegistration = (method) => {
    trackEvent('registration', 'authentication', method)
    
    // Dodatkowe śledzenie Google Ads conversion dla rejestracji
    if (hasGoogleAdsConsent() && isGtagAvailable()) {
      window.gtag('event', 'conversion', {
        send_to: 'AW-1047089755/registration',
        value: 1
      })
    }
  }

  // Śledź dodanie oferty
  const trackAddOffer = () => {
    trackEvent('add_offer', 'content_creation')
    
    // Dodatkowe śledzenie Google Ads conversion dla dodania oferty
    if (hasGoogleAdsConsent() && isGtagAvailable()) {
      window.gtag('event', 'conversion', {
        send_to: 'AW-1047089755/add_offer',
        value: 1
      })
    }
  }

  // Śledź aktywację oferty (Google Ads conversion)
  const trackOfferActivation = (offerId) => {
    if (hasGoogleAdsConsent() && isGtagAvailable()) {
      window.gtag('event', 'conversion', {
        send_to: 'AW-1047089755/offer_activation',
        value: offerId
      })
    }
  }

  // Śledź edycję oferty
  const trackEditOffer = (offerId) => {
    trackEvent('edit_offer', 'content_creation', null, offerId)
  }

  // Automatyczne śledzenie zmian routingu
  const setupRouteTracking = () => {
    watch(
      () => route.path,
      (newPath, oldPath) => {
        if (newPath !== oldPath) {
          // Małe opóźnienie aby strona się załadowała
          setTimeout(() => {
            trackPageView()
          }, 100)
        }
      },
      { immediate: true }
    )
  }

  // Inicjalizacja śledzenia
  onMounted(() => {
    setupRouteTracking()
  })

  return {
    trackPageView,
    trackEvent,
    trackOfferClick,
    trackSearch,
    trackFavorite,
    trackComparison,
    trackLogin,
    trackRegistration,
    trackAddOffer,
    trackOfferActivation,
    trackEditOffer,
    isGtagAvailable
  }
} 